import { useEffect, useRef, type CSSProperties, type JSX } from 'react';
import { Link } from 'react-router';
import { usePostHog } from '@posthog/react';
import { Button, Logo, Wordmark } from '@viniciomendesr/rayrovisk-design-system';
import {
  BUNDLE,
  BUNDLE_PIECES,
  FAQ,
  LOT_FILLED_PERCENT,
  OFFERS,
  brlWithCents,
  bundleDiscountPercent,
  bundleSavings,
  bundleTotal,
  checkout,
  type Product
} from './salesContent';
import { ProfileReadingDialog } from './ProfileReadingDialog';
import { useProfileReadingInvite } from './useProfileReadingInvite';

const VIMEO_VIDEO_ID = '1217460878';
const VIMEO_PLAYER_URL = `https://player.vimeo.com/video/${VIMEO_VIDEO_ID}?autoplay=1&muted=1&badge=0&autopause=0&player_id=sales-vimeo-player&app_id=58479`;
const VIMEO_PLAYER_API_URL = 'https://player.vimeo.com/api/player.js';
const VIMEO_TITLE = 'Garanta seu acesso ao Roteirovisk';

export const RAYRA_PROFILE_URL =
  'https://roteirovisk.com/?utm_source=instagram&utm_medium=organic_social&utm_campaign=rayra_bio&utm_content=profile_link';

const RAYRA_PROFILE_ATTRIBUTION = {
  traffic_source: 'instagram',
  traffic_medium: 'organic_social',
  campaign: 'rayra_bio',
  placement: 'profile_link'
} as const;

const OTHER_ATTRIBUTION = {
  traffic_source: 'other',
  traffic_medium: 'unknown',
  campaign: 'unattributed',
  placement: 'unknown'
} as const;

function salesAttribution(search: string): typeof RAYRA_PROFILE_ATTRIBUTION | typeof OTHER_ATTRIBUTION {
  const params = new URLSearchParams(search);
  const isRayraProfile =
    params.get('utm_source') === 'instagram' &&
    params.get('utm_medium') === 'organic_social' &&
    params.get('utm_campaign') === 'rayra_bio' &&
    params.get('utm_content') === 'profile_link';

  return isRayraProfile ? RAYRA_PROFILE_ATTRIBUTION : OTHER_ATTRIBUTION;
}

type VimeoTimeUpdate = { percent: number };

type VimeoPlayer = {
  on(event: 'play' | 'ended', callback: () => void): void;
  on(event: 'timeupdate', callback: (data: VimeoTimeUpdate) => void): void;
  off(event: 'play' | 'ended', callback: () => void): void;
  off(event: 'timeupdate', callback: (data: VimeoTimeUpdate) => void): void;
};

type VimeoPlayerConstructor = new (element: HTMLIFrameElement) => VimeoPlayer;

declare global {
  interface Window {
    Vimeo?: { Player: VimeoPlayerConstructor };
  }
}

let vimeoPlayerApi: Promise<VimeoPlayerConstructor> | undefined;

function loadVimeoPlayerApi(): Promise<VimeoPlayerConstructor> {
  if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo.Player);
  if (vimeoPlayerApi) return vimeoPlayerApi;

  vimeoPlayerApi = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${VIMEO_PLAYER_API_URL}"]`
    );
    const script = existing ?? document.createElement('script');
    const resolvePlayer = () => {
      if (window.Vimeo?.Player) resolve(window.Vimeo.Player);
      else reject(new Error('Vimeo player API loaded without a Player constructor'));
    };

    script.addEventListener('load', resolvePlayer, { once: true });
    script.addEventListener('error', () => reject(new Error('Vimeo player API failed to load')), {
      once: true
    });

    if (!existing) {
      script.src = VIMEO_PLAYER_API_URL;
      script.async = true;
      document.head.append(script);
    }
  });

  return vimeoPlayerApi;
}

/**
 * The heading, in both senses.
 *
 * `index.html` quotes this sentence and `SUBHEADLINE` verbatim, as the share
 * preview's title and description; `document.test.ts` fails if either stops
 * existing here. They are constants rather than inline JSX for that reason: the
 * test reads this file as text, so a sentence broken across two elements would
 * be a sentence the preview claims and the page does not have.
 */
const HEADLINE = 'Nunca mais abra o Instagram sem saber o que postar.';

/** The half the hero paints orange. Split at render, so `HEADLINE` stays whole. */
const HEADLINE_ACCENT = 'sem saber o que postar.';

const SUBHEADLINE =
  '200 prompts para Reels, Carrossel e Stories e 100 roteiros que o gerador escreve na sua voz. Você troca o nicho e grava.';

/**
 * What she is buying, in three words each, above the fold. Every one of them is
 * a fact the offer keeps: 200 is `COMMITTED_TOTAL_COUNT`, 100 is the generator's
 * credit grant, and the payment really is single.
 */
const PILLS = ['200 prompts customizáveis', '100 roteiros no gerador', 'paga uma vez, não expira'];

const STEPS: Array<{ lead: string; rest: string }> = [
  { lead: 'Você conta a tarefa', rest: 'do jeito que contaria para um amigo.' },
  {
    lead: 'Você troca o que está entre colchetes',
    rest: 'pelo seu nicho, a sua voz e a sua oferta.'
  },
  {
    lead: 'Você cola o prompt na IA que já usa.',
    rest: 'ChatGPT, Claude ou Gemini, no plano gratuito.'
  },
  {
    lead: 'Você grava.',
    rest: 'O roteiro já vem com gancho, tempo de cada take e fechamento.'
  }
];

const EYEBROW: CSSProperties = {
  font: '600 9.5px/1 var(--rvk-font-functional)',
  letterSpacing: '.14em',
  textTransform: 'uppercase'
};

const INK_BORDER = '#494440';
const INK_SURFACE = '#282421';

/**
 * The only public screen with nothing behind it. The price is on it because the
 * offer says so: no form to find out what it costs.
 *
 * One offer since 2026-09-01. The generator's Hotmart price came down to the
 * same R$ 37,00 the starter charges for twenty prompts, so there is no ladder
 * left to draw — `sellableOffers` is what decides, and the JSON-LD reads the
 * same function.
 *
 * Buying leaves for Hotmart through the canonical public hotlink. A build-time
 * override can replace it without changing this component.
 */
export function SalesScreen(): JSX.Element {
  const posthog = usePostHog();
  const pricingRef = useRef<HTMLDivElement>(null);
  const reading = useProfileReadingInvite({
    onEvent: (name, properties) => posthog?.capture(name, properties)
  });

  useEffect(() => {
    posthog?.capture('sales_page_viewed', salesAttribution(window.location.search));

    const reachedDepths = new Set<number>();
    let pricingViewed = false;
    const checkPosition = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight > 0) {
        const depth = Math.min(100, (window.scrollY / scrollableHeight) * 100);
        for (const threshold of [25, 50, 75, 100]) {
          if (depth >= threshold && !reachedDepths.has(threshold)) {
            reachedDepths.add(threshold);
            posthog?.capture('sales_scroll_depth_reached', { depth: threshold });
          }
        }
      }

      const pricingTop = pricingRef.current?.getBoundingClientRect().top;
      if (
        !pricingViewed &&
        document.documentElement.scrollHeight > 0 &&
        pricingTop !== undefined &&
        pricingTop !== 0 &&
        pricingTop <= window.innerHeight
      ) {
        pricingViewed = true;
        posthog?.capture('sales_pricing_viewed', { section: 'pricing' });
      }
    };

    window.addEventListener('scroll', checkPosition, { passive: true });
    checkPosition();
    return () => window.removeEventListener('scroll', checkPosition);
  }, [posthog]);

  const price = brlWithCents(OFFERS.generator.price);

  return (
    <section style={{ minHeight: '100vh', background: 'var(--rvk-color-cream-50)' }}>
      {reading.open ? (
        <ProfileReadingDialog
          onClose={reading.dismiss}
          onEvent={(name, properties) => posthog?.capture(name, properties)}
          // ADR-0025: `setPersonProperties`, never `identify(email)`. This
          // attaches the address to the anonymous id she already carries, so
          // the sign-in `identify` keeps doing the ordinary anonymous→identified
          // merge instead of being handed a second id it cannot reconcile.
          onEmailCaptured={(email) => posthog?.setPersonProperties({ email })}
        />
      ) : null}

      {/* Floating rather than sticky: it is a pill over the hero, not a bar
          bolted to the top of it, and `pointer-events` is off on the wrapper so
          the gap beside it does not swallow clicks on the page underneath. */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 40,
          padding: '12px clamp(10px,3vw,28px)',
          pointerEvents: 'none'
        }}
      >
        <div
          data-theme="dark"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '9px clamp(12px,3vw,28px)',
            maxWidth: 1180,
            margin: '0 auto',
            padding: '9px 10px 9px clamp(16px,2vw,24px)',
            background: INK_SURFACE,
            border: `1px solid ${INK_BORDER}`,
            borderRadius: 28,
            boxShadow: 'var(--rvk-shadow-4)',
            pointerEvents: 'auto'
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minWidth: 0,
              flex: '1 1 min(100%, 200px)'
            }}
          >
            <span
              style={{
                font: '600 12.5px/1 var(--rvk-font-functional)',
                color: 'var(--rvk-color-cream-50)',
                whiteSpace: 'nowrap'
              }}
            >
              Lote com {bundleDiscountPercent()}% OFF
            </span>
            <LotBar width={190} />
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flex: '0 1 auto',
              minWidth: 0,
              marginLeft: 'auto'
            }}
          >
            <Link to="/entrar">
              <Button variant="ghost" size="sm">
                Entrar
              </Button>
            </Link>
            <BuyButton label={`Garantir por ${price}`} size="sm" placement="lot_bar" />
          </span>
        </div>
      </div>

      <section
        data-theme="dark"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(18px,2vw,24px)',
          padding: 'clamp(118px,10vw,128px) clamp(20px,4vw,56px) clamp(44px,5vw,64px)',
          background: 'var(--rvk-color-ink-900)',
          // Explicit, not inherited: `data-theme` swaps the tokens but `body`
          // still sets a colour, and the wordmark draws in `currentColor` —
          // without this it is ink on ink and simply does not appear.
          color: 'var(--rvk-color-cream-50)',
          textAlign: 'center'
        }}
      >
        {/* The product name and, beside it, whose product it is. The name was
            the `h1` once, which meant the one heading a search engine reads was
            a word only people who already know us type. */}
        <span
          className="rvk-rise"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            flexWrap: 'wrap'
          }}
        >
          <span
            style={{
              font: '600 19px/1 var(--rvk-font-functional)',
              letterSpacing: '.2em',
              textTransform: 'uppercase',
              color: 'var(--rvk-color-orange-400)'
            }}
          >
            Roteirovisk
          </span>
          <span style={{ width: 1, height: 15, background: INK_BORDER }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{
                ...EYEBROW,
                font: '500 9px/1 var(--rvk-font-functional)',
                color: 'var(--rvk-color-ink-300)'
              }}
            >
              um produto
            </span>
            <Wordmark size={24} />
          </span>
        </span>

        <span
          className="rvk-rise rvk-rise-2"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            flexWrap: 'wrap'
          }}
        >
          {PILLS.map((pill) => (
            <span
              key={pill}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '10px 16px',
                border: `1px solid ${INK_BORDER}`,
                borderRadius: 999,
                font: '500 10.5px/1 var(--rvk-font-mono)',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--rvk-color-cream-50)'
              }}
            >
              {pill}
            </span>
          ))}
        </span>

        <h1
          className="rvk-rise rvk-rise-2"
          style={{
            margin: '4px 0 0',
            font: '400 clamp(32px,4.1vw,52px)/1.08 var(--rvk-font-expressive)',
            letterSpacing: '-.022em',
            color: 'var(--rvk-color-cream-50)',
            maxWidth: 'min(100%,40ch)',
            textWrap: 'balance'
          }}
        >
          {HEADLINE.replace(HEADLINE_ACCENT, '')}
          <span style={{ color: 'var(--rvk-color-orange-400)' }}>{HEADLINE_ACCENT}</span>
        </h1>

        <p
          className="rvk-rise rvk-rise-3"
          style={{
            margin: 0,
            font: '400 15.5px/1.6 var(--rvk-font-functional)',
            color: 'var(--rvk-color-ink-200)',
            maxWidth: 'min(100%,58ch)'
          }}
        >
          {SUBHEADLINE}
        </p>

        <div className="rvk-rise rvk-rise-4" style={{ width: 'min(100%,720px)', marginTop: 6 }}>
          <Demo />
        </div>

        <div
          className="rvk-rise rvk-rise-5"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            width: 'min(100%,440px)',
            marginTop: 6
          }}
        >
          <BuyButton label="Adquirir os roteiros e prompts" size="lg" fullWidth placement="hero" />

          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              width: '100%'
            }}
          >
            <LotBar width={300} />
            <span
              style={{
                font: '400 12.5px/1.45 var(--rvk-font-functional)',
                color: 'var(--rvk-color-ink-200)'
              }}
            >
              <strong style={{ fontWeight: 600, color: 'var(--rvk-color-orange-400)' }}>
                Últimas vagas
              </strong>{' '}
              do lote de {price}
            </span>
          </span>

          <span
            style={{
              font: '400 12px/1.45 var(--rvk-font-functional)',
              color: 'var(--rvk-color-ink-300)'
            }}
          >
            Fora do lote, o acesso volta para {brlWithCents(bundleTotal())}
          </span>
        </div>
      </section>

      <section
        style={{
          padding: 'clamp(42px,4.6vw,60px) clamp(20px,4vw,56px)',
          background: 'var(--rvk-color-cream-50)'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(20px,2.4vw,30px)',
            width: '100%',
            maxWidth: 1180,
            margin: '0 auto'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h2
              style={{
                margin: 0,
                font: '600 10px/1 var(--rvk-font-functional)',
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                color: 'var(--rvk-text-tertiary)',
                whiteSpace: 'nowrap'
              }}
            >
              Como funciona, em quatro passos
            </h2>
            <span style={{ flex: '1 1 auto', height: 1, background: 'var(--rvk-border-subtle)' }} />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,160px),1fr))',
              gap: 'clamp(18px,2vw,30px)'
            }}
          >
            {STEPS.map((step, index) => (
              <div
                key={step.lead}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  paddingTop: 14,
                  borderTop: '1px solid var(--rvk-border-default)'
                }}
              >
                <span
                  style={{
                    font: '500 11px/1 var(--rvk-font-mono)',
                    letterSpacing: '.08em',
                    color: 'var(--rvk-accent-default)'
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span style={{ font: '400 14px/1.55 var(--rvk-font-functional)' }}>
                  <strong style={{ fontWeight: 600 }}>{step.lead}</strong> {step.rest}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div
        ref={pricingRef}
        id="precos"
        style={{
          padding: 'clamp(48px,5vw,72px) clamp(20px,4vw,56px) clamp(52px,5vw,76px)',
          background: 'var(--rvk-surface-1)',
          // `clip`, not `hidden`: the seal hangs off the card's bottom-right
          // corner and its landing animation starts at 1.45×, which on a phone
          // reaches past the viewport and gives the whole page a horizontal
          // scrollbar. `clip` stops that without making this a scroll container,
          // so the vertical overhang still draws.
          overflowX: 'clip',
          scrollMarginTop: 72
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 620 }}>
            <GuaranteeSeal />

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--rvk-color-cream-50)',
                border: '2px solid var(--rvk-accent-default)',
                borderRadius: 'var(--rvk-radius-xl)',
                boxShadow: 'var(--rvk-shadow-3)',
                overflow: 'hidden',
                width: '100%'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 24px',
                  background: 'var(--rvk-accent-default)'
                }}
              >
                <span style={{ ...EYEBROW, color: 'var(--rvk-accent-fg-on-solid)' }}>
                  Acesso único
                </span>
                <span
                  style={{
                    font: '500 11px/1 var(--rvk-font-functional)',
                    color: 'var(--rvk-accent-fg-on-solid)'
                  }}
                >
                  Paga uma vez · não expira · não renova
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  padding: '24px clamp(18px,3vw,28px) 44px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ ...EYEBROW, color: 'var(--rvk-accent-default)' }}>
                    O pacote inteiro
                  </span>
                  <h2 style={{ margin: 0, font: '400 26px/1.18 var(--rvk-font-expressive)' }}>
                    O que entra na sua conta
                  </h2>
                  <span
                    style={{
                      font: '400 13px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-secondary)'
                    }}
                  >
                    Ao lado, o preço de cada item vendido separado.
                  </span>
                </div>

                {/* Every number in this table is arithmetic on `BUNDLE`: the
                    line prices, the sum, the percentage and the saving. Typing
                    the total by hand is how a page ends up claiming a discount
                    its own prices do not produce. */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {BUNDLE.map((item, index) => (
                    <div
                      key={item.name}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0,1fr) auto',
                        gap: '4px 18px',
                        padding: '13px 0',
                        borderTop: '1px solid var(--rvk-border-subtle)',
                        borderBottom:
                          index === BUNDLE.length - 1
                            ? '1px solid var(--rvk-border-subtle)'
                            : undefined
                      }}
                    >
                      <span
                        style={{
                          gridColumn: 1,
                          font: '500 15px/1.35 var(--rvk-font-functional)'
                        }}
                      >
                        {item.name}
                      </span>
                      <span
                        style={{
                          gridColumn: 2,
                          gridRow: 1,
                          font:
                            item.price === null
                              ? '500 13px/1.35 var(--rvk-font-functional)'
                              : '500 15px/1.35 var(--rvk-font-mono)',
                          fontVariantNumeric: 'tabular-nums',
                          color:
                            item.price === null
                              ? 'var(--rvk-accent-default)'
                              : 'var(--rvk-text-tertiary)',
                          textDecoration: item.price === null ? undefined : 'line-through',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.price === null ? 'Sem custo' : brlWithCents(item.price)}
                      </span>
                      <span
                        style={{
                          gridColumn: 1,
                          font: '400 13px/1.5 var(--rvk-font-functional)',
                          color: 'var(--rvk-text-secondary)'
                        }}
                      >
                        {item.detail}
                      </span>
                    </div>
                  ))}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0,1fr) auto',
                      gap: 18,
                      padding: '15px 0 0'
                    }}
                  >
                    <span style={{ font: '500 14px/1.35 var(--rvk-font-functional)' }}>
                      Somando item por item
                    </span>
                    <span
                      style={{
                        font: '500 17px/1.2 var(--rvk-font-mono)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {brlWithCents(bundleTotal())}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    padding: '18px 20px',
                    background: 'var(--rvk-color-ink-900)',
                    borderRadius: 'var(--rvk-radius-md)'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: 14,
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <span style={{ ...EYEBROW, color: 'var(--rvk-color-ink-300)' }}>
                        Você paga hoje
                      </span>
                      {/* No per-instalment number: Hotmart adds interest to the
                          twelve, so a value printed here is one the checkout
                          does not charge. */}
                      <span
                        style={{
                          font: '400 12.5px/1.4 var(--rvk-font-functional)',
                          color: 'var(--rvk-color-ink-200)'
                        }}
                      >
                        uma vez, ou em até 12×
                      </span>
                    </span>
                    <span
                      style={{ display: 'flex', alignItems: 'baseline', gap: 11, flexWrap: 'wrap' }}
                    >
                      <span
                        style={{
                          font: '400 clamp(42px,4.4vw,54px)/1 var(--rvk-font-expressive)',
                          letterSpacing: '-.022em',
                          color: 'var(--rvk-color-cream-50)'
                        }}
                      >
                        {price}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 10px',
                          borderRadius: 999,
                          background: 'var(--rvk-color-orange-400)',
                          font: '600 11px/1 var(--rvk-font-functional)',
                          letterSpacing: '.04em',
                          color: 'var(--rvk-color-ink-900)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        −{bundleDiscountPercent()}%
                      </span>
                    </span>
                  </div>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 14,
                      flexWrap: 'wrap',
                      paddingTop: 13,
                      borderTop: `1px solid ${INK_BORDER}`
                    }}
                  >
                    <span
                      style={{
                        font: '500 13px/1.4 var(--rvk-font-functional)',
                        color: 'var(--rvk-color-cream-50)'
                      }}
                    >
                      Você deixa de gastar {brlWithCents(bundleSavings())}
                    </span>
                    <span
                      style={{
                        font: '400 12.5px/1.4 var(--rvk-font-functional)',
                        color: 'var(--rvk-color-ink-200)'
                      }}
                    >
                      {brlWithCents(OFFERS.generator.price / BUNDLE_PIECES)} por prompt ou roteiro
                    </span>
                  </span>
                </div>

                <BuyButton label={`Quero acesso por ${price}`} size="lg" fullWidth placement="pricing" />

                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                    paddingRight: 'clamp(72px,9vw,92px)'
                  }}
                >
                  <span
                    style={{
                      font: '400 12.5px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-secondary)'
                    }}
                  >
                    Compra pela Hotmart. O acesso chega no seu e-mail na hora.
                  </span>
                  <span
                    style={{
                      font: '500 12.5px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-accent-default)'
                    }}
                  >
                    Fechado o lote, o mesmo pacote volta para {brlWithCents(bundleTotal())}.
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Last on the page because it is last in the head: she has seen the
            output and the price, and what is left is the reason not to buy.
            Every answer here was already promised somewhere above — this is
            where the promise is repeated as the question she was going to ask
            anyway.

            It is also the only part of the page an assistant can quote whole.
            The same array becomes FAQPage JSON-LD at build time, so the two
            never disagree. */}
        <div
          style={{
            width: '100%',
            maxWidth: 1180,
            margin: 'clamp(40px,4.2vw,56px) auto 0',
            paddingTop: 'clamp(34px,3.6vw,46px)',
            borderTop: '1px solid var(--rvk-border-subtle)'
          }}
        >
          <h2
            style={{
              margin: '0 0 24px',
              font: '400 clamp(26px,2.4vw,32px)/1.15 var(--rvk-font-expressive)'
            }}
          >
            Perguntas que chegam antes da compra
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,290px),1fr))',
              gap: '26px 34px',
              alignItems: 'start'
            }}
          >
            {FAQ.map(({ question, answer }) => (
              <div
                key={question}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}
              >
                <h3 style={{ margin: 0, font: '400 18px/1.3 var(--rvk-font-expressive)' }}>
                  {question}
                </h3>
                <p
                  style={{
                    margin: 0,
                    font: '400 13.5px/1.6 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)',
                    maxWidth: '46ch'
                  }}
                >
                  {answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
          padding: 'clamp(56px,5.6vw,80px) clamp(20px,4vw,56px)',
          textAlign: 'center'
        }}
      >
        <h2
          style={{
            margin: 0,
            font: '400 clamp(30px,3.4vw,44px)/1.12 var(--rvk-font-expressive)',
            letterSpacing: '-.022em',
            maxWidth: '24ch',
            textWrap: 'balance'
          }}
        >
          Você não precisa de mais uma ideia. Precisa do roteiro pronto.
        </h2>
        <BuyButton label={`Quero acesso por ${price}`} size="lg" placement="closing" />
      </section>

      <footer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: 1180,
          margin: '0 auto',
          padding: '24px clamp(20px,4vw,56px) 34px',
          borderTop: '1px solid var(--rvk-border-subtle)'
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
          <Logo variant="symbol" size={26} />
          <span
            style={{
              font: '400 12.5px/1.4 var(--rvk-font-functional)',
              color: 'var(--rvk-text-tertiary)'
            }}
          >
            Roteirovisk · A sua voz, em todo prompt
          </span>
        </span>
        <span
          style={{
            font: '400 12.5px/1.4 var(--rvk-font-functional)',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          Suporte por e-mail · Termos de uso · Privacidade
        </span>
      </footer>
    </section>
  );
}

/**
 * The lot meter. `LOT_FILLED_PERCENT` is a constant nobody measures, which is
 * written down where the constant lives; this component only draws it.
 */
function LotBar({ width }: { width: number }): JSX.Element {
  return (
    <span
      role="presentation"
      style={{
        position: 'relative',
        flex: '1 1 auto',
        minWidth: 40,
        flexShrink: 1,
        width: '100%',
        maxWidth: width,
        height: 7,
        borderRadius: 999,
        background: INK_BORDER,
        overflow: 'hidden'
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          borderRadius: 999,
          background: 'var(--rvk-color-orange-400)',
          width: `${LOT_FILLED_PERCENT}%`
        }}
      />
    </span>
  );
}

/**
 * The seven days, as a stamp on the corner of the card.
 *
 * It is an `img` to a screen reader and nothing at all to a crawler's price
 * parser: the guarantee it names is already in the FAQ and in the JSON-LD's
 * `MerchantReturnPolicy`, which is the copy that has to be machine-readable.
 * `prefers-reduced-motion` stops every animation in `app.css`.
 */
function GuaranteeSeal(): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        bottom: 'max(-46px,-6vw)',
        right: 'max(-46px,-4vw)',
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none'
      }}
    >
      <div
        className="rvk-selo"
        style={{
          position: 'relative',
          flex: 'none',
          width: 'clamp(94px,12vw,128px)',
          height: 'clamp(94px,12vw,128px)',
          transform: 'rotate(-7deg)',
          filter: 'drop-shadow(0 12px 24px rgba(32,29,27,.28))'
        }}
      >
        <span className="rvk-selo-onda" />
        <svg viewBox="0 0 160 160" width="100%" height="100%" role="img" aria-label="Sete dias de garantia" style={{ display: 'block' }}>
          <defs>
            <path id="rvkSeloAro" d="M80,80 m-57,0 a57,57 0 1,1 114,0 a57,57 0 1,1 -114,0" fill="none" />
          </defs>
          <circle cx="80" cy="80" r="78" fill="var(--rvk-color-ink-900)" />
          <circle cx="80" cy="80" r="71.5" fill="none" stroke="var(--rvk-accent-default)" strokeWidth="1.2" opacity="0.55" />
          <circle cx="80" cy="80" r="42" fill="none" stroke="var(--rvk-accent-default)" strokeWidth="1" opacity="0.75" />
          <g className="rvk-selo-aro">
            <text
              style={{
                fontFamily: 'var(--rvk-font-functional)',
                fontSize: '11.5px',
                fontWeight: 600,
                letterSpacing: '0.2em',
                fill: 'var(--rvk-color-cream-50)'
              }}
            >
              <textPath href="#rvkSeloAro" startOffset="0%">
                7 DIAS DE GARANTIA · 7 DIAS DE GARANTIA ·
              </textPath>
            </text>
          </g>
          <text
            x="80"
            y="81"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontFamily: 'var(--rvk-font-expressive)', fontSize: '52px', fill: 'var(--rvk-accent-default)' }}
          >
            7
          </text>
          <text
            x="80"
            y="112"
            textAnchor="middle"
            style={{
              fontFamily: 'var(--rvk-font-functional)',
              fontSize: '8px',
              fontWeight: 600,
              letterSpacing: '0.16em',
              fill: 'var(--rvk-color-ink-200)'
            }}
          >
            DIAS
          </text>
        </svg>
      </div>
    </div>
  );
}

function Demo(): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const posthog = usePostHog();

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let disposed = false;
    let player: VimeoPlayer | undefined;
    let started = false;
    const reached = new Set<number>();
    const properties = {
      provider: 'vimeo',
      surface: 'sales_demo',
      video_id: VIMEO_VIDEO_ID
    };

    const captureProgress = (percent: 25 | 50 | 75 | 100) => {
      if (reached.has(percent)) return;
      reached.add(percent);
      posthog?.capture(percent === 100 ? 'video_completed' : `video_${percent}_percent`, properties);
    };
    const onPlay = () => {
      if (started) return;
      started = true;
      posthog?.capture('video_started', properties);
    };
    const onTimeUpdate = ({ percent }: VimeoTimeUpdate) => {
      if (percent >= 0.25) captureProgress(25);
      if (percent >= 0.5) captureProgress(50);
      if (percent >= 0.75) captureProgress(75);
    };
    const onEnded = () => captureProgress(100);

    void loadVimeoPlayerApi()
      .then((Player) => {
        if (disposed) return;
        player = new Player(iframe);
        player.on('play', onPlay);
        player.on('timeupdate', onTimeUpdate);
        player.on('ended', onEnded);
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      player?.off('play', onPlay);
      player?.off('timeupdate', onTimeUpdate);
      player?.off('ended', onEnded);
    };
  }, [posthog]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        minWidth: 0,
        background: INK_SURFACE,
        border: `1px solid ${INK_BORDER}`,
        borderRadius: 'var(--rvk-radius-xl)',
        boxShadow: 'var(--rvk-shadow-4)'
      }}
    >
      <span
        style={{
          ...EYEBROW,
          padding: '0 2px',
          font: '600 10px/1 var(--rvk-font-functional)',
          letterSpacing: '.16em',
          textAlign: 'left',
          color: 'var(--rvk-color-ink-300)'
        }}
      >
        Veja funcionando
      </span>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          borderRadius: 'var(--rvk-radius-md)',
          background: 'var(--rvk-color-ink-900)'
        }}
      >
        <iframe
          ref={iframeRef}
          id="sales-vimeo-player"
          src={VIMEO_PLAYER_URL}
          title={VIMEO_TITLE}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          frameBorder="0"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
}

/**
 * The one checkout, wherever the page asks for it.
 *
 * `placement` is the only thing that differs between the four buttons, and it
 * is what tells the funnel which one she pressed.
 */
function BuyButton(props: {
  label: string;
  placement: 'lot_bar' | 'hero' | 'pricing' | 'closing';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}): JSX.Element {
  const posthog = usePostHog();
  const product: Product = 'PROMPT_GENERATOR';
  const url = checkout(product);

  // A checkout that resolves to nothing never becomes a link that 404s. The
  // button says so instead.
  if (!url) {
    return (
      <Button variant="primary" size={props.size} fullWidth={props.fullWidth} access="unavailable">
        Em breve
      </Button>
    );
  }

  return (
    <a
      href={url}
      rel="noreferrer"
      style={props.fullWidth ? { display: 'block', width: '100%' } : undefined}
      onClick={() =>
        posthog?.capture('checkout_initiated', {
          product,
          placement: props.placement
        })
      }
    >
      <Button variant="primary" size={props.size} fullWidth={props.fullWidth}>
        {props.label}
      </Button>
    </a>
  );
}
